import { useEffect, useState } from 'react';
import styled from 'styled-components';
import Header from '../components/Header';
import '../index.css';
import '../customTable.css'; // Import the custom CSS for the new table
import CustomTable, { Column } from '../components/CustomTable'; // Import the new CustomTable
import { UserInterface } from '../types';
import moment from 'moment';
import apiClient from '../services/apiClient';
import EmptyState from '../components/EmptyState';
import DashboardIcon from '../assets/svgs/DashboardIcon';
import ReactGA from 'react-ga4';
import { CreateButton } from '../components/SharedComponents';
import UserModal from '../components/UserModal';
import UsersIcon from '../assets/svgs/UsersIcon';

// Extended interface for shop users that includes ownership and other shop-specific properties
interface ShopUserInterface extends UserInterface {
	is_owner?: boolean | number;
	type?: 'user' | 'invitation';
	date_added?: string;
	name?: string;
}

const Wrapper = styled.div`
	background-color: white;
	width: 100%;
	min-height: 100vh;
	padding: 30px 40px;
	box-sizing: border-box;
	@media (max-width: 768px) {
		padding: 20px 20px;
	}
`;

export default function UserManagement() {
	const [isEditPanelVisible, setIsEditPanelVisible] = useState<boolean>(false);
	const [selectedUser, setSelectedUser] = useState<ShopUserInterface | null>(null);
	const [users, setUsers] = useState<ShopUserInterface[]>([]);
	const [fetchState, setFetchState] = useState<'loading' | 'success' | 'failed'>('loading');

	const columns: Column[] = [
		// {
		//     field: 'avatar',
		//     headerName: '',
		//     minWidth: 80,
		//     type: 'image',
		//     headerClassName: "table-header",
		//     cellClassName: 'text-center',
		//     isNotSearchable: true,
		//     renderCell: (params) => params.value ? (
		//         <img
		//             src={params.value}
		//             alt={`${params.row.name} avatar`}
		//             className="w-10 h-10 rounded-full object-cover"
		//         />
		//     ) : (
		//         <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
		//             <DashboardIcon width={24} height={24} />
		//         </div>
		//     ),
		//     flex: 0.5
		// },
		{
			field: 'name',
			headerName: 'Name',
			minWidth: 200,
			headerClassName: 'table-header',
			cellClassName: 'font-medium text-gray-900',
			flex: 1.5,
		},
		{
			field: 'email',
			headerName: 'Email',
			minWidth: 220,
			headerClassName: 'table-header',
			cellClassName: 'text-gray-500',
			flex: 2,
		},
		{
			field: 'is_owner',
			headerName: 'Role',
			minWidth: 100,
			headerClassName: 'table-header',
			cellClassName: 'text-gray-500',
			renderCell: (params) => (
				<span
					className={`px-2 py-1 rounded-full text-xs ${
						params.value === true ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
					}`}
				>
					{params.value === true ? 'Owner' : 'User'}
				</span>
			),
			flex: 1,
		},
		{
			field: 'type',
			headerName: 'Status',
			minWidth: 100,
			headerClassName: 'table-header',
			cellClassName: 'text-gray-500',
			renderCell: (params) =>
				params.value === 'invitation' ? (
					<span className="px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-800">Pending</span>
				) : null,
			flex: 1,
		},
		{
			field: 'date_added',
			headerName: 'Date Added',
			minWidth: 150,
			headerClassName: 'table-header',
			cellClassName: 'text-gray-500',
			renderCell: (params) => moment(params.value).format('Do MMM YYYY'),
			flex: 1,
		},
	];

	useEffect(() => {
		ReactGA.send({
			hitType: 'pageview',
			page: '/users',
			title: 'User Management',
		});
		setFetchState('loading');
		apiClient
			.get('/shops/users')
			.then((response: any) => {
				setFetchState('success');
				console.log('fetched users', response.data);
				const formattedUsers = response.data.map((user: ShopUserInterface) => {
					let name = user.firstname + ' ' + user.surname;
					if (user.firstname === null || user.surname === null) {
						name = 'N/A';
					}

					return {
						...user,
						name,
					};
				});
				setUsers(formattedUsers);
				ReactGA.event({
					category: 'Users',
					action: 'Fetched Users',
					label: 'Fetch Success',
				});
			})
			.catch((error) => {
				console.log('error fetching users', error);
				setFetchState('failed');
				ReactGA.event({
					category: 'Users',
					action: 'Fetch Users Failed',
					label: 'Fetch Failed',
				});
			});
	}, []);

	useEffect(() => {
		console.log('users updated', users);
	}, [users]);

	const handleRowClick = (params: { row: ShopUserInterface }) => {
		const user = params.row;
		ReactGA.event({
			category: 'User Interaction',
			action: 'Clicked User Row',
			label: `User ID: ${user.id}`,
		});
		setSelectedUser(user);
		setIsEditPanelVisible(true);
	};

	const showInviteUserPanel = () => {
		ReactGA.event({
			category: 'User Interaction',
			action: 'Clicked Invite User Button',
			label: 'Invite User',
		});
		setIsEditPanelVisible(true);
	};

	const hideUserPanel = (user: ShopUserInterface | null = null) => {
		setIsEditPanelVisible(false);
		setSelectedUser(null);

		if (user) {
			let isUserNew = true;

			setUsers(
				users.map((u: ShopUserInterface) => {
					if (u.id === user.id) {
						isUserNew = false;
						return user;
					}
					return u;
				})
			);

			if (isUserNew) {
				setUsers([...users, user]);
				ReactGA.event({
					category: 'Users',
					action: 'Invited New User',
					label: `User ID: ${user.id}`,
				});
			} else {
				ReactGA.event({
					category: 'Users',
					action: 'Updated User',
					label: `User ID: ${user.id}`,
				});
			}
		} else {
			ReactGA.event({
				category: 'User Interaction',
				action: 'Closed User Panel',
				label: 'No User',
			});
		}
	};

	const handleUserRemoved = () => {
		if (selectedUser) {
			// Remove the user from the list
			setUsers(users.filter((u: ShopUserInterface) => u.id !== selectedUser.id));
			ReactGA.event({
				category: 'Users',
				action: 'Removed User',
				label: `User ID: ${selectedUser.id}`,
			});
		}
	};

	return (
		<Wrapper>
			<Header
				title="Manage Users"
				description="Manage this business's users and their permissions"
				headerComponents={[
					<CreateButton key="invite-button" onClick={showInviteUserPanel}>
						<UsersIcon />
						<span>Invite User</span>
					</CreateButton>,
				]}
			/>
			<CustomTable
				columns={columns}
				objects={users}
				sx={{ marginTop: '50px' }}
				onRowClick={(param: { row: UserInterface }) => handleRowClick(param)}
				emptyStateComponent={() => (
					<EmptyState
						handleButtonClick={showInviteUserPanel}
						title="No Team Members Yet"
						description="Start by inviting team members to collaborate. You can set their roles and permissions to control their access levels."
						buttonText="Invite User"
						icon={<DashboardIcon width={50} height={50} />}
					/>
				)}
				fetchState={fetchState}
				initialRowsPerPage={25}
			/>
			<UserModal
				isVisible={isEditPanelVisible}
				handleHidePanel={hideUserPanel}
				user={selectedUser}
				onUserRemoved={handleUserRemoved}
			/>
		</Wrapper>
	);
}
